// DealFiltersBar — панель фильтров списка сделок (R17b.1).
// Стадии (multi-select chips) + тумблер поля дат (U-27: «Создана | Следующий контакт |
// Плановая дата закрытия») + диапазон дат («С даты»/«По дату») + кнопка «Сбросить».
// Даты конвертируются в ISO Instant по FIX-C канону: instant-поля — локальный день →
// UTC (startOfDay/endOfDay), dueDate — прежний UTC-суффикс.
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { endOfDay, isValid, parseISO, startOfDay } from 'date-fns';
import { DatePickerField } from '@/components/forms';
import type { DealDateField, DealStage } from '@/api/deals';
import { t } from '@/i18n';
import { formatStage } from '@/lib/format';

/** Стадии в порядке отображения в фильтре. */
const STAGE_ORDER: DealStage[] = ['NEW', 'QUALIFIED', 'NEGOTIATION', 'PROPOSAL', 'WON', 'LOST'];

/** Суффикс начала дня для dueDate-границ (UTC-день, прежний канон). */
const DAY_START_SUFFIX = 'T00:00:00.000Z';

/** Суффикс конца дня для dueDate-границ (UTC-день, прежний канон). */
const DAY_END_SUFFIX = 'T23:59:59.999Z';

/** Минимальная ширина контейнера с двумя DatePicker (px). */
const DATE_RANGE_MIN_WIDTH_PX = 480;

/** Поля формы диапазона дат (DatePickerField хранит «yyyy-MM-dd»). */
interface DealDateFields {
  dueAfter: string | null;
  dueBefore: string | null;
}

interface DealFiltersBarProps {
  /** Выбранные стадии. */
  stages: DealStage[];
  /** Переключение стадии в multi-select. */
  onStageToggle: (stage: DealStage) => void;
  /** Изменение диапазона дат (ISO Instant либо null). */
  onDatesChange: (dueAfter: string | null, dueBefore: string | null) => void;
  /** Выбранное поле дат-диапазона (U-27). */
  dateField: DealDateField;
  /** Смена поля дат-диапазона (U-27). */
  onDateFieldChange: (field: DealDateField) => void;
  /** Полный сброс фильтров (стадии + даты + сортировка). */
  onReset: () => void;
}

/**
 * Границы дня как ISO Instant: {@code dueDate} — UTC-день (LocalDate → суффикс),
 * instant-поля — локальный день → UTC (FIX-C канон {@code DayEventsPage}).
 * Невалидная строка (например, «2026-02-31») → null — фильтр игнорируется,
 * а не RangeError (F-1 pintest: parseISO → startOfDay → toISOString).
 */
export function toIsoBound(localDate: string | null, mode: DealDateField, isStart: boolean): string | null {
  if (!localDate) return null;
  const parsed = parseISO(localDate);
  if (!isValid(parsed)) return null;
  if (mode === 'dueDate') {
    return isStart ? `${localDate}${DAY_START_SUFFIX}` : `${localDate}${DAY_END_SUFFIX}`;
  }
  return (isStart ? startOfDay(parsed) : endOfDay(parsed)).toISOString();
}

/** Панель фильтров списка сделок. */
export function DealFiltersBar({
  stages,
  onStageToggle,
  onDatesChange,
  dateField,
  onDateFieldChange,
  onReset,
}: DealFiltersBarProps): JSX.Element {
  const { control, watch, reset } = useForm<DealDateFields>({
    defaultValues: { dueAfter: null, dueBefore: null },
  });

  // Пробрасываем изменения дат наверх в виде ISO Instant с учётом выбранного поля.
  useEffect(() => {
    const subscription = watch((values) => {
      onDatesChange(
        toIsoBound(values.dueAfter ?? null, dateField, true),
        toIsoBound(values.dueBefore ?? null, dateField, false),
      );
    });
    return () => subscription.unsubscribe();
  }, [watch, onDatesChange, dateField]);

  const handleReset = (): void => {
    reset({ dueAfter: null, dueBefore: null });
    onReset();
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 2 }}
      data-testid="deal-filters-bar"
    >
      <Typography variant="body2" color="text.secondary">
        Стадия:
      </Typography>
      {STAGE_ORDER.map((stage) => {
        const selected = stages.includes(stage);
        return (
          <Chip
            key={stage}
            data-testid={`stage-filter-${stage}`}
            label={formatStage(stage, t)}
            clickable
            onClick={() => onStageToggle(stage)}
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'filled' : 'outlined'}
            size="small"
          />
        );
      })}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={dateField}
        onChange={(_, value) => {
          if (value) onDateFieldChange(value as DealDateField);
        }}
        aria-label="Поле даты фильтра"
        data-testid="deal-date-field-toggle"
      >
        <ToggleButton value="createdAt" data-testid="deal-date-field-createdAt">
          {t('dealDateField.createdAt')}
        </ToggleButton>
        <ToggleButton value="nextContactAt" data-testid="deal-date-field-nextContactAt">
          {t('dealDateField.nextContactAt')}
        </ToggleButton>
        <ToggleButton value="dueDate" data-testid="deal-date-field-dueDate">
          {t('dealDateField.dueDate')}
        </ToggleButton>
      </ToggleButtonGroup>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: DATE_RANGE_MIN_WIDTH_PX }}>
        <DatePickerField
          name="dueAfter"
          control={control}
          label="С даты"
          aria-label="С даты"
        />
        <DatePickerField
          name="dueBefore"
          control={control}
          label="По дату"
          aria-label="По дату"
        />
      </Stack>
      <Button variant="text" onClick={handleReset}>
        Сбросить
      </Button>
    </Stack>
  );
}
